import { useEffect, useMemo } from 'react';
import { DatePicker, Form, Input, InputNumber, Modal, Segmented, Select, Switch, Timeline, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { saveBillingRequest, setBillingPrice, setBillingStatus } from '@/services/teamBilling.service';

const useSaved = (onClose) => {
  const { t } = useTranslation('teamBilling');
  const queryClient = useQueryClient();
  return {
    onSuccess: () => {
      message.success(t('messages.saved'));
      queryClient.invalidateQueries({ queryKey: ['team-billing'] });
      onClose();
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  };
};

// With no deadline, or one already past, the work counts as done: the same rule as the server's.
const resolvedByDefault = (dueDate) => !dueDate || !dayjs(dueDate).isAfter(dayjs(), 'day');

const toNumber = (value) => (value === null || value === undefined || value === '' ? null : value);

function PriceFields({ currencies, form, withSwitches }) {
  const { t } = useTranslation('teamBilling');
  return (
    <>
      <div className="grid grid-cols-[minmax(0,1fr)_120px] gap-3">
        <Form.Item name="price" label={t('form.price')} extra={t('form.priceHint')}>
          <InputNumber className="w-full" min={0.01} max={1000000000000} precision={2} decimalSeparator="," />
        </Form.Item>
        <Form.Item name="currency" label={t('form.currency')}>
          <Select options={currencies.map((value) => ({ value, label: value }))} />
        </Form.Item>
      </div>
      {withSwitches && (
        <Form.Item noStyle shouldUpdate={(a, b) => a.price !== b.price}>
          {() =>
            toNumber(form.getFieldValue('price')) === null ? null : (
              <>
                <Form.Item name="clientConfirmed" valuePropName="checked" label={t('form.clientConfirmed')} extra={t('form.clientConfirmedHint')}>
                  <Switch />
                </Form.Item>
                <Form.Item name="resolved" valuePropName="checked" label={t('form.resolved')} extra={t('form.resolvedHint')}>
                  <Switch />
                </Form.Item>
              </>
            )
          }
        </Form.Item>
      )}
    </>
  );
}

const withCurrent = (options, id, name) =>
  id && !options.some((o) => o._id === id) ? [{ _id: id, name: name || id }, ...options] : options;

/** Opens a request or changes what it says; read-only for whoever may not change it. */
export function BillingRequestDialog({ open, request, options, onClose }) {
  const { t } = useTranslation('teamBilling');
  const [form] = Form.useForm();
  const isNew = !request;
  const readOnly = !isNew && !request.canEdit;
  const clientType = Form.useWatch('clientType', form);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(
      request
        ? {
            ...request,
            clientType: request.company ? 'company' : 'free',
            dueDate: request.dueDate ? dayjs(request.dueDate) : null,
          }
        : {
            clientType: 'company',
            currency: 'RSD',
            requestedBy: options.employeeId || undefined,
            clientConfirmed: true,
            resolved: true,
          }
    );
  }, [open, request, form, options.employeeId]);

  const save = useMutation({
    mutationFn: ({ clientType: type, ...v }) => {
      const base = {
        _id: request?._id,
        title: v.title,
        description: v.description || '',
        company: type === 'company' ? v.company || null : null,
        clientName: type === 'free' ? v.clientName || '' : '',
        requestedBy: v.requestedBy || null,
        lead: v.lead || null,
        dueDate: v.dueDate ? v.dueDate.format('YYYY-MM-DD') : null,
      };
      if (!isNew) return saveBillingRequest(options.canManage ? { ...base, invoiceNote: v.invoiceNote || '' } : base);
      const price = toNumber(v.price);
      return saveBillingRequest(
        price === null ? base : { ...base, price, currency: v.currency, clientConfirmed: !!v.clientConfirmed, resolved: !!v.resolved }
      );
    },
    ...useSaved(onClose),
  });

  const companies = useMemo(
    () => withCurrent(options.companies, request?.company, request?.client),
    [options.companies, request]
  );
  const people = useMemo(
    () =>
      withCurrent(withCurrent(options.people, request?.lead, request?.leadName), request?.requestedBy, request?.requestedByName),
    [options.people, request]
  );
  const asOptions = (list) => list.map((o) => ({ value: o._id, label: o.name }));
  const required = [{ required: true, message: t('form.required') }];
  const dialogTitle = isNew ? 'dialogs.create' : readOnly ? 'dialogs.view' : 'dialogs.edit';

  return (
    <Modal
      open={open}
      title={t(dialogTitle)}
      width={640}
      onCancel={onClose}
      onOk={() => (readOnly ? onClose() : form.submit())}
      okText={t(readOnly ? 'actions.close' : 'actions.save')}
      cancelText={t('actions.close')}
      confirmLoading={save.isPending}
      okButtonProps={{ shape: 'round' }}
      cancelButtonProps={{ shape: 'round', style: readOnly ? { display: 'none' } : undefined }}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        disabled={readOnly}
        onFinish={(v) => save.mutate(v)}
        onValuesChange={(changed) => {
          if ('dueDate' in changed && !form.isFieldTouched('resolved')) {
            form.setFieldsValue({ resolved: resolvedByDefault(changed.dueDate) });
          }
        }}
      >
        <Form.Item name="title" label={t('form.title')} rules={required}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="clientType" label={t('form.clientType')}>
          <Segmented
            options={[
              { value: 'company', label: t('form.clientCompany') },
              { value: 'free', label: t('form.clientFree') },
            ]}
          />
        </Form.Item>
        {clientType === 'free' ? (
          <Form.Item name="clientName" label={t('form.clientName')} rules={required}>
            <Input maxLength={200} />
          </Form.Item>
        ) : (
          <Form.Item name="company" label={t('form.company')} rules={required}>
            <Select showSearch optionFilterProp="label" options={asOptions(companies)} />
          </Form.Item>
        )}
        <Form.Item name="description" label={t('form.description')}>
          <Input.TextArea rows={3} maxLength={4000} />
        </Form.Item>
        <div className="grid grid-cols-3 gap-3">
          <Form.Item name="requestedBy" label={t('form.requestedBy')}>
            <Select allowClear showSearch optionFilterProp="label" options={asOptions(people)} />
          </Form.Item>
          <Form.Item name="lead" label={t('form.lead')}>
            <Select allowClear showSearch optionFilterProp="label" options={asOptions(people)} />
          </Form.Item>
          <Form.Item name="dueDate" label={t('form.dueDate')}>
            <DatePicker className="w-full" format="DD.MM.YYYY" />
          </Form.Item>
        </div>
        {isNew && options.canManage && <PriceFields currencies={options.currencies} form={form} withSwitches />}
        {!isNew && options.canManage && (
          <Form.Item name="invoiceNote" label={t('form.invoiceNote')} extra={t('form.invoiceNoteHint')}>
            <Input maxLength={500} />
          </Form.Item>
        )}
      </Form>
      {!isNew && request.history?.length > 0 && (
        <>
          <div className="mb-3 font-semibold">{t('dialogs.history')}</div>
          <Timeline
            className="mb-0"
            items={request.history.map((h) => ({
              children: `${dayjs(h.at).format('DD.MM.YYYY HH:mm')} · ${t(`history.${h.event}`)} · ${h.by || t('history.system')}`,
            }))}
          />
        </>
      )}
    </Modal>
  );
}

/** The agreed price and the client's confirmation, in one step. */
export function BillingPriceDialog({ request, options, onClose }) {
  const { t } = useTranslation('teamBilling');
  const [form] = Form.useForm();

  useEffect(() => {
    if (!request) return;
    form.resetFields();
    form.setFieldsValue({
      price: request.price,
      currency: request.currency,
      clientConfirmed: true,
      resolved: !!request.resolved || resolvedByDefault(request.dueDate),
    });
  }, [request, form]);

  const save = useMutation({
    mutationFn: (v) => {
      const price = toNumber(v.price);
      return setBillingPrice({
        _id: request._id,
        price,
        currency: v.currency,
        clientConfirmed: price !== null && !!v.clientConfirmed,
        resolved: !!v.resolved,
      });
    },
    ...useSaved(onClose),
  });

  return (
    <Modal
      open={!!request}
      title={request ? `${t('dialogs.price')}: ${request.title}` : ''}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t('actions.save')}
      cancelText={t('actions.close')}
      confirmLoading={save.isPending}
      okButtonProps={{ shape: 'round' }}
      cancelButtonProps={{ shape: 'round' }}
    >
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <PriceFields currencies={options.currencies} form={form} withSwitches />
      </Form>
    </Modal>
  );
}

/** Marks the request as billed, or corrects the note on one already billed. */
export function BillingBilledDialog({ request, onClose }) {
  const { t } = useTranslation('teamBilling');
  const [form] = Form.useForm();

  useEffect(() => {
    if (!request) return;
    form.resetFields();
    form.setFieldsValue({ invoiceNote: request.invoiceNote || '' });
  }, [request, form]);

  const save = useMutation({
    mutationFn: ({ invoiceNote }) =>
      request.status === 'BILLED'
        ? saveBillingRequest({ _id: request._id, invoiceNote: invoiceNote || '' })
        : setBillingStatus({ _id: request._id, status: 'BILLED', invoiceNote: invoiceNote || '' }),
    ...useSaved(onClose),
  });

  return (
    <Modal
      open={!!request}
      title={request ? `${t('dialogs.billed')}: ${request.title}` : ''}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t('actions.save')}
      cancelText={t('actions.close')}
      confirmLoading={save.isPending}
      okButtonProps={{ shape: 'round' }}
      cancelButtonProps={{ shape: 'round' }}
    >
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Form.Item name="invoiceNote" label={t('form.invoiceNote')} extra={t('form.invoiceNoteHint')}>
          <Input maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
